import { Check, Clock, ExclamationMark, type Icon, X } from '@phosphor-icons/react';
import { cn, Inline } from '@reelplicate/ui';

import facebook9 from './assets/mark-facebook-9.svg';
import facebook10 from './assets/mark-facebook-10.svg';
import facebook16 from './assets/mark-facebook-16.svg';
import instagram9 from './assets/mark-instagram-9.svg';
import instagram10 from './assets/mark-instagram-10.svg';
import instagram16 from './assets/mark-instagram-16.svg';
import tiktok9 from './assets/mark-tiktok-9.svg';
import tiktok10 from './assets/mark-tiktok-10.svg';
import tiktok16 from './assets/mark-tiktok-16.svg';
import youtube9 from './assets/mark-youtube-9.svg';
import youtube10 from './assets/mark-youtube-10.svg';
import youtube16 from './assets/mark-youtube-16.svg';
import type { AccountPostState } from './calendar-model';
import { type Platform, PLATFORM_NAMES } from './platforms';

// White Figma glyphs (Mark/* recoloured) on the platform's brand colour,
// which is intentional for third-party logos.
const PLATFORMS: Record<Platform, { bg: string } & Record<DotSize, string>> = {
  tiktok: { bg: 'bg-[#111]', sm: tiktok9, md: tiktok10, lg: tiktok16 },
  instagram: { bg: 'bg-[#e1306c]', sm: instagram9, md: instagram10, lg: instagram16 },
  youtube: { bg: 'bg-[#ff0000]', sm: youtube9, md: youtube10, lg: youtube16 },
  facebook: { bg: 'bg-[#1877f2]', sm: facebook9, md: facebook10, lg: facebook16 },
};

type DotSize = 'sm' | 'md' | 'lg';

const SIZES: Record<DotSize, string> = {
  sm: 'size-4 border-[1.5px]',
  md: 'size-[18px] border-[1.5px]',
  lg: 'size-8',
};

/**
 * A platform mark in a brand-colour circle. `md` (18px, 10px glyph, white
 * ring) sits in post chips and the legend; `sm` (16px, 9px glyph, white ring)
 * is the badge on an account avatar; `lg` (32px, 16px glyph) leads an
 * account row in Analytics.
 */
export function PlatformDot({
  platform,
  size = 'md',
  className,
}: {
  platform: Platform;
  size?: DotSize;
  className?: string;
}) {
  const { bg, ...glyphs } = PLATFORMS[platform];
  return (
    <span
      className={cn(
        'border-bg-surface flex shrink-0 items-center justify-center rounded-full',
        SIZES[size],
        bg,
        className,
      )}
    >
      <img src={glyphs[size]} alt="" />
    </span>
  );
}

/** A platform a post goes to, with where it stands there. */
export type PlatformMark = {
  platform: Platform;
  state?: AccountPostState;
  /** Why it failed there; shown on hover. */
  failureReason?: string | null;
};

/**
 * The badge on a dot for states worth flagging. Upcoming posts, drafts and
 * cancelled accounts get none, to keep the chips quiet.
 */
const BADGES: Partial<
  Record<AccountPostState, { label: string; icon: Icon; className: string }>
> = {
  published: {
    label: 'published',
    icon: Check,
    className: 'bg-status-success-solid text-text-inverse',
  },
  checking: {
    label: 'checking',
    icon: Clock,
    className: 'bg-bg-muted text-text-secondary',
  },
  missing: {
    label: 'not found yet',
    icon: ExclamationMark,
    className: 'bg-status-warning-solid text-text-primary',
  },
  failed: {
    label: 'failed',
    icon: X,
    className: 'bg-status-danger-solid text-text-inverse',
  },
};

const markOf = (item: Platform | PlatformMark): PlatformMark =>
  typeof item === 'string' ? { platform: item } : item;

/** "TikTok", or "TikTok: failed (Not found on TikTok …)" when it has a badge. */
function markLabel({ platform, state, failureReason }: PlatformMark): string {
  const badge = state && BADGES[state];
  if (!badge) return PLATFORM_NAMES[platform];
  const label = `${PLATFORM_NAMES[platform]}: ${badge.label}`;
  return state === 'failed' && failureReason ? `${label} (${failureReason})` : label;
}

/**
 * Overlapping platform dots: several = cross-posted (Figma "Platforms"). Given
 * per-account states, each dot carries a status badge (published, checking,
 * not found yet, failed) and the dots spread out so every badge shows.
 */
export function PlatformStack({
  platforms,
  className,
}: {
  platforms: (Platform | PlatformMark)[];
  className?: string;
}) {
  const marks = platforms.map(markOf);
  const badged = marks.some((m) => m.state && BADGES[m.state]);
  return (
    <Inline
      as="span"
      gap={badged ? 1 : 0}
      role="img"
      aria-label={marks.map(markLabel).join(', ')}
      className={className}
    >
      {marks.map((mark, i) => {
        const badge = mark.state && BADGES[mark.state];
        const overlap = !badged && i < marks.length - 1 ? '-mr-[5px]' : undefined;
        if (!badge) {
          return <PlatformDot key={i} platform={mark.platform} className={overlap} />;
        }
        const BadgeIcon = badge.icon;
        return (
          <span key={i} title={markLabel(mark)} className="relative shrink-0">
            <PlatformDot platform={mark.platform} />
            <span
              data-state={mark.state}
              className={cn(
                'border-bg-surface absolute -right-[3px] -bottom-[3px] flex size-[11px] items-center justify-center rounded-full border',
                badge.className,
              )}
            >
              <BadgeIcon weight="bold" className="size-[7px]" />
            </span>
          </span>
        );
      })}
    </Inline>
  );
}
